import {
  Suspense,
  lazy,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { RedirectToSignIn, useAuth } from "@clerk/react";
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import * as Sentry from "@sentry/react";
import { ProLayout } from "./components/layout/ProLayout";
import { RouteDocumentTitle } from "./components/layout/RouteDocumentTitle";
import { ScrollToTop } from "./components/layout/ScrollToTop";
import { ProAuthGate } from "./components/auth/ProAuthGate";
import { useCopilotEnabled } from "./hooks/useCopilotEnabled";
import { useFirstRunWelcome } from "./hooks/useFirstRunWelcome";
import { TodayGroupSidebar } from "./components/today/TodayGroupSidebar";
import { MarketplaceMobileNav } from "./components/marketplace/MarketplaceMobileNav";
import { MarketplaceSidebar } from "./pages/marketplace/MarketplaceSidebar";
import { useMarketplaceRoutesRegistered } from "./hooks/useMarketplaceEnabled";
import { useLenderMarketplaceGate } from "./hooks/useLenderMarketplaceGate";
import { useProToken } from "./hooks/useProToken";
import { LoginPage } from "./pages/LoginPage";
import { SetupSecurityPreviewPage } from "./pages/SetupSecurityPreviewPage";
import { JoinPage } from "./pages/JoinPage";
import { ApiError, getJSON } from "./lib/api";
import { IS_PREVIEW } from "./lib/pro-mode";
import { personReturnRoute, personRoute, PRO_ROUTE_PATHS } from "./lib/routes";

// Wraps react-router's Routes so Sentry attaches a transaction per resolved
// route — without this every navigation lands in the same anonymous span.
const SentryRoutes = Sentry.withSentryReactRouterV7Routing(Routes);

const TasksPage = lazy(async () => {
  const m = await import("./pages/TasksPage");
  return { default: m.TasksPage };
});

const HomePage = lazy(async () => {
  const m = await import("./pages/HomePage");
  return { default: m.HomePage };
});

const WelcomePage = lazy(async () => {
  const m = await import("./pages/WelcomePage");
  return { default: m.WelcomePage };
});

const JoinOfferPage = lazy(async () => {
  const m = await import("./pages/JoinOfferPage");
  return { default: m.JoinOfferPage };
});

const CampaignsPage = lazy(async () => {
  const m = await import("./pages/CampaignsPage");
  return { default: m.CampaignsPage };
});

const PeoplePage = lazy(async () => {
  const m = await import("./pages/PeoplePage");
  return { default: m.PeoplePage };
});
const PersonDetailPage = lazy(async () => {
  const m = await import("./pages/PersonDetailPage");
  return { default: m.PersonDetailPage };
});

const ActivityPage = lazy(async () => {
  const m = await import("./pages/ActivityPage");
  return { default: m.ActivityPage };
});

const ProfilePage = lazy(async () => {
  const m = await import("./pages/ProfilePage");
  return { default: m.ProfilePage };
});

const SettingsPage = lazy(async () => {
  const m = await import("./pages/SettingsPage");
  return { default: m.SettingsPage };
});

const PricingPage = lazy(async () => {
  const m = await import("./pages/PricingPage");
  return { default: m.PricingPage };
});

const TemplatesPage = lazy(async () => {
  const m = await import("./pages/TemplatesPage");
  return { default: m.TemplatesPage };
});

const AutomationsPage = lazy(async () => {
  const m = await import("./pages/AutomationsPage");
  return { default: m.AutomationsPage };
});

const MarketplaceProfilePage = lazy(async () => {
  const m = await import("./pages/marketplace/MarketplaceProfilePage");
  return { default: m.MarketplaceProfilePage };
});

const MarketplaceOffersPage = lazy(async () => {
  const m = await import("./pages/marketplace/MarketplaceOffersPage");
  return { default: m.MarketplaceOffersPage };
});

const MarketplaceBrowsePage = lazy(async () => {
  const m = await import("./pages/marketplace/MarketplaceBrowsePage");
  return { default: m.MarketplaceBrowsePage };
});

const MarketplaceComposerPage = lazy(async () => {
  const m = await import("./pages/marketplace/MarketplaceComposerPage");
  return { default: m.MarketplaceComposerPage };
});

const ClientFormalOfferPage = lazy(async () => {
  const m = await import("./pages/marketplace/MarketplaceComposerPage");
  return { default: m.ClientFormalOfferPage };
});

const MarketplaceLedgerPage = lazy(async () => {
  const m = await import("./pages/marketplace/MarketplaceLedgerPage");
  return { default: m.MarketplaceLedgerPage };
});

const MarketplaceLandingGate = lazy(async () => {
  const m = await import("./pages/marketplace/MarketplaceLandingGate");
  return { default: m.MarketplaceLandingGate };
});

const MarketplaceRequestsPage = lazy(async () => {
  const m = await import("./pages/marketplace/MarketplaceRequestsPage");
  return { default: m.MarketplaceRequestsPage };
});

const MarketplaceRequestDetailPage = lazy(async () => {
  const m = await import("./pages/marketplace/MarketplaceRequestDetailPage");
  return { default: m.MarketplaceRequestDetailPage };
});

const MarketplaceConnectionsPage = lazy(async () => {
  const m = await import("./pages/marketplace/MarketplaceConnectionsPage");
  return { default: m.MarketplaceConnectionsPage };
});

const NotificationsPage = lazy(async () => {
  const m = await import("./pages/NotificationsPage");
  return { default: m.NotificationsPage };
});

const MessagesPage = lazy(async () => {
  const m = await import("./pages/MessagesPage");
  return { default: m.MessagesPage };
});

const AssistantPage = lazy(async () => {
  const m = await import("./pages/AssistantPage");
  return { default: m.AssistantPage };
});

export function App() {
  const marketplaceEnabled = useMarketplaceRoutesRegistered();
  return (
    <BrowserRouter>
      <RouteDocumentTitle />
      <ScrollToTop />
      <SentryRoutes>
        {/* Login lives OUTSIDE the authenticated shell (no sidebar, no gate).
            The splat lets Clerk's path-based <SignIn> own its sub-routes
            (SSO callback, verification steps). */}
        <Route path="/login/*" element={<LoginPage />} />
        {/* DEV fixture for the 2-step setup screen; the page itself redirects
            unless IS_PREVIEW (no Clerk key + Vite dev build). */}
        <Route
          path="/preview/setup-security"
          element={<SetupSecurityPreviewPage />}
        />
        {/* Public Pro signup (plan 476 M3): also outside the shell — the visitor
            has no account yet. The offer step after it is an authed route. */}
        <Route path={PRO_ROUTE_PATHS.join} element={<JoinPage />} />
        <Route
          path={PRO_ROUTE_PATHS.joinOffer}
          element={
            // Onboarding, not the app: signed-in gate only, no ProLayout chrome.
            <ProAuthGate>
              <RouteFallback>
                <JoinOfferPage />
              </RouteFallback>
            </ProAuthGate>
          }
        />
        {/* Everything else mounts the signed-in gate inside the Pro shell. */}
        <Route path="/" element={<Navigate replace to="/home" />} />
        <Route
          path="/home"
          element={
            <AuthedLayout>
              <HomePage />
            </AuthedLayout>
          }
        />
        {/* Post-signup onboarding (referral link + Sill Pro activation) —
            reached once, right after Clerk signup completes. */}
        <Route
          path={PRO_ROUTE_PATHS.welcome}
          element={
            <AuthedLayout>
              <WelcomePage />
            </AuthedLayout>
          }
        />
        {/* The Today route is retired in favour of Home. Automation-draft links
            already sent by email/SMS (the old route + ?draft=<id>) keep
            working: they land on the Tasks queue with the draft id intact. */}
        <Route path={PRO_ROUTE_PATHS.legacyToday} element={<LegacyTodayRoute />} />
        {/* The work list (formerly Today) lives at /tasks. */}
        <Route
          path={PRO_ROUTE_PATHS.tasks}
          element={
            <AuthedLayout pageSidebar={<TodayGroupSidebar variant="desktop" />}>
              <TasksPage />
            </AuthedLayout>
          }
        />
        {/* Legacy /clients and /contacts routes are retired: People is the
            sole roster surface (M2, D11). M4 deleted the pages/components
            these used to mount; these routes legitimately remain as
            redirect-only compatibility for stale bookmarks/links. */}
        <Route
          path={PRO_ROUTE_PATHS.clients}
          element={<LegacyClientsRoute />}
        />
        <Route
          path={PRO_ROUTE_PATHS.clientDetail}
          element={<LegacyClientDetailRoute />}
        />
        <Route
          path="/campaigns"
          element={
            <AuthedLayout>
              <CampaignsPage />
            </AuthedLayout>
          }
        />
        <Route
          path={PRO_ROUTE_PATHS.contacts}
          element={<LegacyContactsRoute />}
        />
        <Route
          path={PRO_ROUTE_PATHS.people}
          element={
            <AuthedLayout>
              <PeoplePage />
            </AuthedLayout>
          }
        />
        <Route
          path={PRO_ROUTE_PATHS.personDetail}
          element={
            <AuthedLayout>
              <PersonDetailPage />
            </AuthedLayout>
          }
        />
        <Route
          path="/activity"
          element={
            <AuthedLayout>
              <ActivityPage />
            </AuthedLayout>
          }
        />
        <Route
          path="/profile"
          element={
            <AuthedLayout>
              <ProfilePage />
            </AuthedLayout>
          }
        />
        <Route
          path={PRO_ROUTE_PATHS.settings}
          element={
            <AuthedLayout>
              <SettingsPage />
            </AuthedLayout>
          }
        />
        {/* Auth-free fixture seam for D16 visual verification. In a DEV build
            without Clerk, `/preview/settings/referrals` loads the same Settings
            surface with referral fixtures (`?referrals=empty|error`). */}
        <Route
          path="/preview/settings/referrals"
          element={
            <AuthedLayout>
              <SettingsPage />
            </AuthedLayout>
          }
        />
        <Route
          path={PRO_ROUTE_PATHS.pricing}
          element={
            <AuthedLayout>
              <PricingPage />
            </AuthedLayout>
          }
        />
        <Route
          path="/templates"
          element={
            <AuthedLayout>
              <TemplatesPage />
            </AuthedLayout>
          }
        />
        <Route
          path="/automations"
          element={
            <AuthedLayout>
              <AutomationsPage />
            </AuthedLayout>
          }
        />
        {/* Marketplace surface is gated by the runtime `useMarketplaceEnabled`
            flag (MARKETPLACE_FLAG_KEY) — the nav entry (HeaderNav) and every
            route below sit behind ONE PostHog flag. When off, these routes are
            not registered and /marketplace/* falls through to the "*" catch-all
            → /home. Card-on-file (Settings → Billing) is intentionally NOT
            gated; only the marketplace surface is. */}
        {marketplaceEnabled && (
          <>
            <Route
              path={PRO_ROUTE_PATHS.clientFormalOffer}
              element={
                <AuthedLayout marketplace>
                  <ClientFormalOfferPage />
                </AuthedLayout>
              }
            />
            {/* /marketplace → posture-aware landing (M5-F): never-submitted lands
            on Profile, submitted lands on Browse buyers. */}
            <Route
              path="/marketplace"
              element={
                <AuthedLayout marketplace
                  pageSidebar={<MarketplaceSidebar />}
                  pageMobileNav={<MarketplaceMobileNav />}
                >
                  <MarketplaceLandingGate />
                </AuthedLayout>
              }
            />
            <Route
              path="/marketplace/profile"
              element={
                <AuthedLayout marketplace
                  pageSidebar={<MarketplaceSidebar />}
                  pageMobileNav={<MarketplaceMobileNav />}
                >
                  <MarketplaceProfilePage />
                </AuthedLayout>
              }
            />
            {/* Standing offers (renamed from /marketplace/offers — M5 steer). */}
            <Route
              path="/marketplace/standing-offers"
              element={
                <AuthedLayout marketplace
                  pageSidebar={<MarketplaceSidebar />}
                  pageMobileNav={<MarketplaceMobileNav />}
                >
                  <MarketplaceOffersPage />
                </AuthedLayout>
              }
            />
            {/* Browse buyers (landing for a submitted pro) + the passport
            detail/composer flow (task:m5-browse-page, task:m5-composer-page). */}
            <Route
              path="/marketplace/browse"
              element={
                <AuthedLayout marketplace
                  pageSidebar={<MarketplaceSidebar />}
                  pageMobileNav={<MarketplaceMobileNav />}
                >
                  <MarketplaceBrowsePage />
                </AuthedLayout>
              }
            />
            {/* Counter-offer deep link. Static, and ahead of `:passportID`, so
            "recovery" is never treated as a passport id (staging 500). */}
            <Route
              path={PRO_ROUTE_PATHS.marketplaceBrowseRecovery}
              element={
                <AuthedLayout marketplace
                  pageSidebar={<MarketplaceSidebar />}
                  pageMobileNav={<MarketplaceMobileNav />}
                >
                  <MarketplaceComposerPage recovery />
                </AuthedLayout>
              }
            />
            <Route
              path="/marketplace/browse/:passportID"
              element={
                <AuthedLayout marketplace
                  pageSidebar={<MarketplaceSidebar />}
                  pageMobileNav={<MarketplaceMobileNav />}
                >
                  <MarketplaceComposerPage />
                </AuthedLayout>
              }
            />
            {/* Offers ledger (task:m5-pro-ledger-page). */}
            <Route
              path={PRO_ROUTE_PATHS.marketplaceOffers}
              element={
                <AuthedLayout marketplace
                  pageSidebar={<MarketplaceSidebar />}
                  pageMobileNav={<MarketplaceMobileNav />}
                >
                  <MarketplaceLedgerPage />
                </AuthedLayout>
              }
            />
            {/* Buyer connection requests inbox (plan 239, M6-B). */}
            <Route
              path="/marketplace/requests"
              element={
                <AuthedLayout marketplace
                  pageSidebar={<MarketplaceSidebar />}
                  pageMobileNav={<MarketplaceMobileNav />}
                >
                  <MarketplaceRequestsPage />
                </AuthedLayout>
              }
            />
            {/* Request detail — full-page "View offer" surface (M9.7, replaces
              the RequestDetailDrawer). */}
            <Route
              path={PRO_ROUTE_PATHS.marketplaceRequestDetail}
              element={
                <AuthedLayout marketplace
                  pageSidebar={<MarketplaceSidebar />}
                  pageMobileNav={<MarketplaceMobileNav />}
                >
                  <MarketplaceRequestDetailPage />
                </AuthedLayout>
              }
            />
            {/* Connections ledger — every buyer the pro is currently paying
              for / has paid for (plan 239, M6-B). */}
            <Route
              path="/marketplace/connections"
              element={
                <AuthedLayout marketplace
                  pageSidebar={<MarketplaceSidebar />}
                  pageMobileNav={<MarketplaceMobileNav />}
                >
                  <MarketplaceConnectionsPage />
                </AuthedLayout>
              }
            />
          </>
        )}
        <Route
          path="/notifications"
          element={
            <AuthedLayout>
              <NotificationsPage />
            </AuthedLayout>
          }
        />
        {/* Messages inbox (messaging-inbox IA WS2/WS5): a full-page
            workspace reached from the header speech-bubble icon — a
            bell-style crossing, deliberately NOT an 8th primary nav tab
            (the primary-tab promotion is decided in the D22 nav pass). */}
        <Route
          path="/messages"
          element={
            <AuthedLayout>
              <MessagesPage />
            </AuthedLayout>
          }
        />
        {/* /assistant is the copilot full-page surface (scenario 678) — NOT
            wrapped in ProLayout (it owns its own back-to-CRM top bar / rail /
            column chrome) and NOT a primary nav destination (reached only
            via the dock's expand icon); still behind the same auth gate. */}
        <Route
          path="/assistant"
          element={
            <ProAuthGate>
              <RequireCopilot>
                <RouteFallback>
                  <AssistantPage />
                </RouteFallback>
              </RequireCopilot>
            </ProAuthGate>
          }
        />
        <Route path="*" element={<Navigate replace to="/home" />} />
      </SentryRoutes>
    </BrowserRouter>
  );
}

// The Today route is retired: Home is the landing route. The one query that must
// survive is an automation-draft deep link (`?draft=<id>`), which moves to the
// Tasks queue. Only the draft id is carried; an empty id is no id.
function LegacyTodayRoute() {
  const location = useLocation();
  const draftID = new URLSearchParams(location.search).get("draft");
  if (!draftID) return <Navigate replace to="/home" />;
  return (
    <Navigate replace to={`/tasks?draft=${encodeURIComponent(draftID)}`} />
  );
}

// Legacy /clients and /contacts routes are retired (M2, D11): People is the
// sole roster surface, unconditionally. These routes exist only to redirect
// a stale bookmark or in-flight link onto the equivalent People destination;
// they never render the legacy pages.
function LegacyClientsRoute() {
  const location = useLocation();
  return (
    <Navigate replace to={`/people${location.search}${location.hash}`} />
  );
}

function LegacyContactsRoute() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const contactID = params.get("contact")?.trim();
  if (!contactID) {
    return (
      <Navigate replace to={`/people${location.search}${location.hash}`} />
    );
  }
  // The `contact` key is consumed above to build the canonical detail path —
  // redundant once it's part of the path itself, so it's stripped rather
  // than carried along as a duplicate query param.
  params.delete("contact");
  const search = params.toString();
  return (
    <Navigate
      replace
      to={`/people/${encodeURIComponent(contactID)}${search ? `?${search}` : ""}${location.hash}`}
    />
  );
}

// Resolves a legacy profile-keyed /clients/:profileId link onto the canonical
// /people/:contactId detail route.
//
// M5 briefly dropped this resolution on the theory that only stale bookmarks
// were affected. They are not: marketplace requests/connections, activity
// rows, in-app notification rows, and deep links already sent in email all
// still emit profile-keyed URLs (they hold a profile id and nothing else), so
// without resolution every one of them silently landed on the roster with the
// person lost. The translation now runs through the Person-native
// `GET /people?profile_id=…` filter rather than the retired /clients shim.
function LegacyClientDetailRoute() {
  const { profileId } = useParams<{ profileId: string }>();
  const location = useLocation();
  const getToken = useProToken();

  // Preview deliberately mounts without Clerk. It retains the existing
  // provider-free token seam; production delegates readiness to Clerk below.
  if (IS_PREVIEW) {
    return (
      <AuthedLayout>
        <LegacyClientDetailResolver
          profileId={profileId}
          search={location.search}
          hash={location.hash}
          getToken={getToken}
        />
      </AuthedLayout>
    );
  }

  return (
    <AuthedLayout>
      <AuthenticatedLegacyClientDetailResolver
        profileId={profileId}
        search={location.search}
        hash={location.hash}
      />
    </AuthedLayout>
  );
}

function AuthenticatedLegacyClientDetailResolver({
  profileId,
  search,
  hash,
}: {
  profileId: string | undefined;
  search: string;
  hash: string;
}) {
  const { getToken, isLoaded, isSignedIn } = useAuth();
  const getTokenRef = useRef(getToken);
  const readToken = useCallback(() => getTokenRef.current(), []);

  useEffect(() => {
    getTokenRef.current = getToken;
  }, [getToken]);

  // Clerk has not yet hydrated (or is still establishing a session). Preserve
  // the legacy URL and keep the compatibility resolver entirely dormant.
  if (!isLoaded || isSignedIn === undefined) return <PeopleRouteLoading />;

  // Mirror ProAuthGate: an explicitly signed-out session goes to Clerk rather
  // than being misreported as an unresolvable client/person relationship.
  if (!isSignedIn) return <RedirectToSignIn />;

  return (
    <LegacyClientDetailResolver
      profileId={profileId}
      search={search}
      hash={hash}
      getToken={readToken}
      authAware
    />
  );
}

function LegacyClientDetailResolver({
  profileId,
  search,
  hash,
  getToken,
  authAware = false,
}: {
  profileId: string | undefined;
  search: string;
  hash: string;
  getToken: () => Promise<string | null>;
  authAware?: boolean;
}) {
  const navigate = useNavigate();
  const [authFailed, setAuthFailed] = useState(false);
  // React StrictMode replays effects after cleanup. Cache the token promise by
  // the full legacy intent (not a "request started" boolean): the replay
  // shares its token read while its own, live effect owns the resolver fetch.
  // A changed profile/intent gets a fresh promise and the prior effect's
  // cancellation guard prevents any late navigation.
  const tokenRunRef = useRef<{
    key: string;
    token: Promise<string | null>;
  } | null>(null);

  useEffect(() => {
    if (!profileId || authFailed) return;
    let cancelled = false;
    const controller = new AbortController();
    const key = `${profileId}\u0000${search}\u0000${hash}\u0000${authAware}`;
    let tokenRun = tokenRunRef.current;
    if (!tokenRun || tokenRun.key !== key) {
      tokenRun = { key, token: getToken() };
      tokenRunRef.current = tokenRun;
      // Share only a token read still in flight. Keeping a fulfilled token in
      // this ref could cross a Clerk session refresh; both outcomes evict it.
      void tokenRun.token.then(
        () => {
          if (tokenRunRef.current === tokenRun) tokenRunRef.current = null;
        },
        () => {
          if (tokenRunRef.current === tokenRun) tokenRunRef.current = null;
        },
      );
    }
    void (async () => {
      try {
        const token = await tokenRun.token;
        if (cancelled || controller.signal.aborted) return;
        if (!token && authAware) {
          setAuthFailed(true);
          return;
        }
        // A FILTER on the one roster read, not a lookup route: D2 keeps
        // ListPeople + GetPerson as the entire read surface, and the roster is
        // already owner-scoped, so a profile this pro has no contact for comes
        // back empty rather than resolving someone else's person.
        // The wire DTO is FLAT (design D4): contact_id sits at the top level of
        // each row, not nested under `contact` the way the Go struct embeds it.
        const resolved = await getJSON<{ people?: { contact_id: string }[] }>(
          `/api/professional/people?profile_id=${encodeURIComponent(profileId)}&limit=1`,
          token ?? undefined,
          controller.signal,
        );
        if (cancelled || controller.signal.aborted) return;
        const contactID = resolved.people?.[0]?.contact_id;
        if (!contactID) {
          navigate("/people", {
            replace: true,
            state: { legacyPersonUnavailable: true },
          });
          return;
        }
        const returnTo = personReturnRoute(
          new URLSearchParams(search).get("returnTo"),
        );
        navigate(
          `${personRoute(contactID, PRO_ROUTE_PATHS.people, returnTo ?? undefined)}${hash}`,
          { replace: true },
        );
      } catch (err) {
        if (cancelled || controller.signal.aborted) return;
        if (authAware && err instanceof ApiError && err.status === 401) {
          setAuthFailed(true);
          return;
        }
        navigate("/people", {
          replace: true,
          state: { legacyPersonUnavailable: true },
        });
      }
    })();
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [authAware, authFailed, getToken, hash, navigate, profileId, search]);

  if (authFailed) return <RedirectToSignIn />;
  return <PeopleRouteLoading />;
}

function PeopleRouteLoading() {
  return (
    <div
      className="flex min-h-[60vh] flex-1 items-center justify-center"
      aria-label="Loading People"
    >
      <div className="text-center">
        <div className="mx-auto mt-0 mb-2 size-8 animate-[spin_0.8s_linear_infinite] rounded-full border-2 border-[color-mix(in_oklch,var(--pro)_25%,transparent)] border-t-pro" />
        <p className="view-sub">Loading People…</p>
      </div>
    </div>
  );
}

/** One component identity for every signed-in shell route. An extra wrapper
 * around marketplace routes remounted ProLayout/HeaderNav on Home ↔ Marketplace
 * transitions, resetting billing-backed chrome (including the earned Pro badge).
 * Marketplace gating changes the page slots, never the shell's position. */
function AuthedLayout({
  marketplace = false,
  pageSidebar,
  pageMobileNav,
  children,
}: {
  marketplace?: boolean;
  pageSidebar?: React.ReactNode;
  pageMobileNav?: React.ReactNode;
  children: React.ReactNode;
}) {
  const lender = useLenderMarketplaceGate();
  const canShowPage = !marketplace || lender === "on";
  return (
    <ProAuthGate>
      {/* Mounted only after auth resolves, never while signing in. */}
      <FirstRunWelcomeMount />
      <ProLayout
        pageSidebar={canShowPage ? pageSidebar : undefined}
        pageMobileNav={canShowPage ? pageMobileNav : undefined}
      >
        <RouteFallback>
          {canShowPage ? children : lender === "off" ? (
            <Navigate replace to="/home" />
          ) : (
            <div
              data-testid="marketplace-gate-loading"
              className="flex min-h-[60vh] flex-1 items-center justify-center"
            >
              <p className="view-sub">Loading workspace…</p>
            </div>
          )}
        </RouteFallback>
      </ProLayout>
    </ProAuthGate>
  );
}

// See the doc comment on AuthedLayout above for why this is a separate
// mounted component rather than a direct useFirstRunWelcome() call in
// AuthedLayout's body.
function FirstRunWelcomeMount() {
  useFirstRunWelcome();
  return null;
}

// RequireCopilot gates the `/assistant` full-page surface behind the same
// PostHog rollout / kill-switch as the dock + header pill (useCopilotEnabled).
// When the copilot is disabled the route redirects to the CRM home rather than
// rendering the assistant — a pro who can't see the opener shouldn't reach the
// page by typing the URL.
function RequireCopilot({ children }: { children: React.ReactNode }) {
  const enabled = useCopilotEnabled();
  if (!enabled) {
    return <Navigate replace to="/people" />;
  }
  return <>{children}</>;
}

function RouteFallback({ children }: { children: React.ReactNode }) {
  return (
    <Suspense
      fallback={
        <div className="flex min-h-[60vh] flex-1 items-center justify-center">
          <div className="text-center">
            <div className="mx-auto mt-0 mb-2 size-8 animate-[spin_0.8s_linear_infinite] rounded-full border-2 border-[color-mix(in_oklch,var(--pro)_25%,transparent)] border-t-pro" />
            <p className="view-sub">Loading workspace…</p>
          </div>
        </div>
      }
    >
      {children}
    </Suspense>
  );
}
